import { type ChangeEvent, type FormEvent, useMemo, useState } from 'react';
import {
  buildCapitalPartnerApplicationMailto,
  CAPITAL_PARTNER_INVESTOR_TYPE_OPTIONS,
  EMPTY_CAPITAL_PARTNER_APPLICATION_FORM,
  type CAPITAL_PARTNER_NETWORK_CONTENT,
  type CapitalPartnerApplicationFormState,
} from '../content';
import {
  ApplicationLayout,
  ContentContainer,
  Field,
  FieldGrid,
  FieldLabel,
  Form,
  FormAside,
  FormError,
  FormHelper,
  FormShell,
  LegalNote,
  Section,
  SectionEyebrow,
  SectionIntro,
  SectionLead,
  SectionTitle,
  SelectInput,
  SubmitButton,
  TextArea,
  TextInput,
} from '../styles';

type ApplicationCopy = (typeof CAPITAL_PARTNER_NETWORK_CONTENT)['application'];

type ApplicationSectionProps = {
  copy: ApplicationCopy;
};

const REQUIRED_FIELDS: Array<keyof CapitalPartnerApplicationFormState> = [
  'fullName',
  'email',
  'investorType',
];

export default function ApplicationSection({ copy }: ApplicationSectionProps) {
  const [form, setForm] = useState<CapitalPartnerApplicationFormState>(
    EMPTY_CAPITAL_PARTNER_APPLICATION_FORM,
  );
  const [error, setError] = useState<string | null>(null);

  const requiredFieldLabels = useMemo(
    () =>
      REQUIRED_FIELDS.map((fieldName) => copy.fields[fieldName])
        .filter(Boolean)
        .join(', '),
    [copy.fields],
  );

  function updateField(
    fieldName: keyof CapitalPartnerApplicationFormState,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const nextValue = event.target.value;
    setForm((current) => ({
      ...current,
      [fieldName]: nextValue,
    }));
    if (error) {
      setError(null);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const missingFields = REQUIRED_FIELDS.filter(
      (fieldName) => !form[fieldName].trim(),
    );

    if (missingFields.length > 0) {
      setError(
        `Please complete: ${missingFields
          .map((fieldName) => copy.fields[fieldName])
          .join(', ')}.`,
      );
      return;
    }

    window.location.href = buildCapitalPartnerApplicationMailto(form);
  }

  return (
    <Section id="application-form">
      <ContentContainer>
        <SectionIntro $wide>
          <SectionEyebrow>{copy.eyebrow}</SectionEyebrow>
          <SectionTitle>{copy.title}</SectionTitle>
          <SectionLead>{copy.intro}</SectionLead>
        </SectionIntro>
        <ApplicationLayout>
          <FormAside>
            <FormShell>
              <SectionLead>
                Share how you invest, what you prioritize, and where you create the most leverage for a business.
              </SectionLead>
              <LegalNote>{copy.microcopy}</LegalNote>
            </FormShell>
          </FormAside>
          <FormShell>
            <Form onSubmit={handleSubmit} noValidate>
              <FieldGrid>
                <Field>
                  <FieldLabel>{copy.fields.fullName}</FieldLabel>
                  <TextInput
                    value={form.fullName}
                    onChange={(event) => updateField('fullName', event)}
                    placeholder={copy.fields.fullName}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.title}</FieldLabel>
                  <TextInput
                    value={form.title}
                    onChange={(event) => updateField('title', event)}
                    placeholder={copy.fields.title}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.firmName}</FieldLabel>
                  <TextInput
                    value={form.firmName}
                    onChange={(event) => updateField('firmName', event)}
                    placeholder={copy.fields.firmName}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.email}</FieldLabel>
                  <TextInput
                    type="email"
                    value={form.email}
                    onChange={(event) => updateField('email', event)}
                    placeholder={copy.fields.email}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.phone}</FieldLabel>
                  <TextInput
                    value={form.phone}
                    onChange={(event) => updateField('phone', event)}
                    placeholder={copy.fields.phone}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.websiteOrLinkedIn}</FieldLabel>
                  <TextInput
                    value={form.websiteOrLinkedIn}
                    onChange={(event) => updateField('websiteOrLinkedIn', event)}
                    placeholder={copy.fields.websiteOrLinkedIn}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.investorType}</FieldLabel>
                  <SelectInput
                    value={form.investorType}
                    onChange={(event) => updateField('investorType', event)}
                  >
                    <option value="">Select investor type</option>
                    {CAPITAL_PARTNER_INVESTOR_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.typicalCheckSize}</FieldLabel>
                  <TextInput
                    value={form.typicalCheckSize}
                    onChange={(event) => updateField('typicalCheckSize', event)}
                    placeholder={copy.fields.typicalCheckSize}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.preferredStages}</FieldLabel>
                  <TextInput
                    value={form.preferredStages}
                    onChange={(event) => updateField('preferredStages', event)}
                    placeholder={copy.fields.preferredStages}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.sectorFocus}</FieldLabel>
                  <TextInput
                    value={form.sectorFocus}
                    onChange={(event) => updateField('sectorFocus', event)}
                    placeholder={copy.fields.sectorFocus}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.geographicPreferences}</FieldLabel>
                  <TextInput
                    value={form.geographicPreferences}
                    onChange={(event) => updateField('geographicPreferences', event)}
                    placeholder={copy.fields.geographicPreferences}
                  />
                </Field>
                <Field>
                  <FieldLabel>{copy.fields.preferredDealTypes}</FieldLabel>
                  <TextInput
                    value={form.preferredDealTypes}
                    onChange={(event) => updateField('preferredDealTypes', event)}
                    placeholder={copy.fields.preferredDealTypes}
                  />
                </Field>
              </FieldGrid>

              <Field>
                <FieldLabel>{copy.fields.whatMattersMost}</FieldLabel>
                <TextArea
                  value={form.whatMattersMost}
                  onChange={(event) => updateField('whatMattersMost', event)}
                  placeholder={copy.fields.whatMattersMost}
                />
              </Field>

              <Field>
                <FieldLabel>{copy.fields.valueBeyondCapital}</FieldLabel>
                <TextArea
                  value={form.valueBeyondCapital}
                  onChange={(event) => updateField('valueBeyondCapital', event)}
                  placeholder={copy.fields.valueBeyondCapital}
                />
              </Field>

              <Field>
                <FieldLabel>{copy.fields.relevantExperience}</FieldLabel>
                <TextArea
                  value={form.relevantExperience}
                  onChange={(event) => updateField('relevantExperience', event)}
                  placeholder={copy.fields.relevantExperience}
                />
              </Field>

              <Field>
                <FieldLabel>{copy.fields.avoids}</FieldLabel>
                <TextArea
                  value={form.avoids}
                  onChange={(event) => updateField('avoids', event)}
                  placeholder={copy.fields.avoids}
                />
              </Field>

              <Field>
                <FieldLabel>{copy.fields.comments}</FieldLabel>
                <TextArea
                  value={form.comments}
                  onChange={(event) => updateField('comments', event)}
                  placeholder={copy.fields.comments}
                />
              </Field>

              <FormHelper>Required to start: {requiredFieldLabels}.</FormHelper>
              {error ? <FormError>{error}</FormError> : null}
              <SubmitButton type="submit">{copy.submitLabel}</SubmitButton>
              <LegalNote>{copy.microcopy}</LegalNote>
            </Form>
          </FormShell>
        </ApplicationLayout>
      </ContentContainer>
    </Section>
  );
}
